// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * `charsight-report.html` — the file you attach to a ticket.
 *
 * Three properties it must have, in this order of importance:
 *
 *  1. **It quotes the payload in full.** Truncating the decoded message would defeat the purpose;
 *     the message IS the finding. It is quoted twice — chipped, so every hidden code point is
 *     visible as a token, and as a hex dump, which is the only lossless form.
 *  2. **It is inert.** No `<script>`, no `<link>`, no `src=`, no remote anything. It is opened by
 *     someone who has just been told a file is suspicious, and it must not itself reach the
 *     network. A test asserts the absence of all four.
 *  3. **It cannot be turned against its reader.** Every piece of text that came from the input goes
 *     through `safeHtmlText`, which chips before it escapes — so a payload containing U+202E cannot
 *     reorder the report's own prose in the reader's browser.
 */

import { CATEGORY_META } from '../scan/catalogue';
import { TIER_LABELS, type Tier } from '../scan/types';
import type { FileResult, RunResult } from '../types';
import { UCD_VERSION } from '../data/unicode';
import { describeCodePoint, escapeHtml, hexDump, safeHtmlText } from './visible';
import { mechanismLabel } from '../scan/htmlscan';

const STYLE = `
:root { color-scheme: light dark; }
* { box-sizing: border-box; }
body { margin: 0 auto; padding: 2rem 1.25rem 4rem; max-width: 60rem;
  font: 15px/1.6 ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  color: #14181f; background: #fff; }
h1 { font-size: 1.5rem; margin: 0 0 .25rem; letter-spacing: -.02em; }
h2 { font-size: 1.1rem; margin: 2.5rem 0 .75rem; padding-bottom: .35rem; border-bottom: 1px solid #dfe3ea; }
h3 { font-size: .95rem; margin: 1.5rem 0 .35rem; }
p { max-width: 46rem; }
.sub { color: #5b6273; margin: 0 0 1.5rem; font-size: .85rem; }
.payload { border: 1px solid #dfe3ea; border-left: 3px solid #b0641b; border-radius: 8px;
  padding: 1rem 1.15rem; margin: 1rem 0; background: #fdfaf5; }
.payload .where { font: .78rem/1.5 ui-monospace, SFMono-Regular, Menlo, monospace; color: #5b6273; }
.quote { font: .9rem/1.7 ui-monospace, SFMono-Regular, Menlo, monospace; white-space: pre-wrap;
  word-break: break-word; background: #fff; border: 1px solid #e6e9ef; border-radius: 6px;
  padding: .7rem .8rem; margin: .6rem 0 .4rem; }
.hex { font: .72rem/1.6 ui-monospace, monospace; color: #5b6273; word-break: break-all; }
.mech { font-size: .85rem; color: #3d4350; }
table { border-collapse: collapse; width: 100%; font-size: .82rem; margin-top: .5rem; }
th, td { text-align: left; padding: .35rem .6rem; border-bottom: 1px solid #eceff4; vertical-align: top; }
th { color: #5b6273; font-weight: 500; }
td.n { text-align: right; font-variant-numeric: tabular-nums; }
code { font: .85em ui-monospace, SFMono-Regular, Menlo, monospace; }
.note { background: #f5f7fa; border: 1px solid #e6e9ef; border-radius: 8px; padding: 1rem 1.15rem;
  font-size: .85rem; color: #3d4350; }
.note strong { color: #14181f; }
footer { margin-top: 3rem; padding-top: 1rem; border-top: 1px solid #dfe3ea;
  font-size: .78rem; color: #5b6273; }
@media (prefers-color-scheme: dark) {
  body { color: #e6e9f0; background: #12151b; }
  h2 { border-color: #262c37; }
  .sub, .payload .where, .hex, th { color: #98a0b2; }
  .payload { background: #1a1710; border-color: #262c37; }
  .quote { background: #0d1015; border-color: #262c37; }
  th, td { border-color: #1e232c; }
  .note { background: #171b22; border-color: #262c37; color: #c3c9d6; }
  .note strong { color: #e6e9f0; }
  footer { border-color: #262c37; }
}
`;

const HONESTY = `
<div class="note">
  <p><strong>What this report cannot tell you.</strong> Charsight reports what is in the bytes. It
  does not, and cannot, tell you who or what wrote this text. Non-breaking spaces, narrow no-break
  spaces, curly quotes, em dashes and soft hyphens come from word processors, keyboard layouts,
  publishing systems, content management systems and ordinary typing in languages other than
  English. A count of them is not evidence about a person, and no part of this tool produces one.</p>
  <p>A clean result means nothing matched the rules in this build of charsight. It is not a
  statement that a file is safe: a file can carry perfectly visible instructions that are every bit
  as dangerous as hidden ones, and new hiding techniques appear faster than detector tables are
  updated.</p>
</div>`;

function payloadBlock(file: string, f: FileResult['findings'][number]): string {
  const decoded = f.decoded ?? '';
  const hex = f.decodedHex ?? (decoded ? hexDump(decoded) : '');
  return `
  <div class="payload">
    <div class="where">${escapeHtml(file)} · line ${f.line}, column ${f.column} · byte ${f.byteStart}–${f.byteEnd} · ${f.runLength} code points</div>
    <p class="mech">${escapeHtml(f.reason)}</p>
    ${decoded ? `<div class="quote">${safeHtmlText(decoded)}</div>` : '<p class="mech">No readable text — see the bytes below.</p>'}
    ${hex ? `<div class="hex">${escapeHtml(hex)}</div>` : ''}
  </div>`;
}

export function buildReport(run: RunResult, profileName: string): string {
  const payloads: string[] = [];
  for (const file of run.files) {
    for (const f of file.findings) {
      if (f.severity === 'payload') payloads.push(payloadBlock(file.name, f));
    }
  }

  const counted = new Map<string, { n: number; tier: Tier; label: string }>();
  for (const file of run.files) {
    for (const f of file.findings) {
      if (f.severity === 'expected') continue;
      const meta = CATEGORY_META[f.category];
      const cur = counted.get(f.category) ?? { n: 0, tier: meta.tier, label: meta.label };
      cur.n += 1;
      counted.set(f.category, cur);
    }
  }
  const summaryRows = [...counted.entries()]
    .sort((a, b) => a[1].tier - b[1].tier || b[1].n - a[1].n)
    .map(
      ([, v]) =>
        `<tr><td>${escapeHtml(v.label)}</td><td>${escapeHtml(TIER_LABELS[v.tier])}</td><td class="n">${v.n}</td></tr>`,
    )
    .join('');

  const perFile = run.files
    .map((f) => {
      const acted = f.findings.filter((x) => x.severity !== 'expected').length;
      const route =
        f.route === 'skipped' ? `not read — ${escapeHtml(f.skipReason ?? 'skipped')}` : f.route;
      return `<tr><td><code>${escapeHtml(f.name)}</code></td><td>${route}</td><td>${escapeHtml(f.encoding ?? '')}</td><td class="n">${f.codePoints || ''}</td><td class="n">${acted}</td></tr>`;
    })
    .join('');

  const occurrences: string[] = [];
  for (const file of run.files) {
    for (const f of file.findings) {
      if (f.severity === 'expected' || f.severity === 'payload') continue;
      occurrences.push(
        `<tr><td><code>${escapeHtml(file.name)}</code></td><td class="n">${f.line}</td><td class="n">${f.column}</td><td class="n">${f.byteStart}</td><td><code>${escapeHtml(f.cp >= 0 ? describeCodePoint(f.cp) : '—')}</code></td><td>${escapeHtml(CATEGORY_META[f.category].label)}</td><td>${escapeHtml(f.reason)}</td></tr>`,
      );
    }
  }

  const htmlBlocks = run.files
    .flatMap((file) =>
      (file.htmlFindings ?? []).map(
        (h) =>
          `<tr><td><code>${escapeHtml(file.name)}</code></td><td><code>${escapeHtml(h.where)}</code></td><td>${escapeHtml(mechanismLabel(h.mechanism))}</td><td><code>${escapeHtml(h.how)}</code></td><td>${safeHtmlText(h.text.slice(0, 400))}</td></tr>`,
      ),
    )
    .join('');

  const pdfBlocks = run.files
    .flatMap((file) =>
      (file.pdfRuns ?? []).map(
        (r) =>
          `<tr><td><code>${escapeHtml(file.name)}</code></td><td class="n">${r.page}</td><td>${escapeHtml(r.reasons.join(', '))}</td><td class="n">${r.glyphCount}</td><td>${r.recoverable ? safeHtmlText(r.text) : `<em>${escapeHtml(r.unrecoverableReason)}</em>`}</td></tr>`,
      ),
    )
    .join('');

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Charsight report</title>
<style>${STYLE}</style>
</head><body>
<h1>Charsight report</h1>
<p class="sub">${escapeHtml(run.at)} · ${run.files.length} file${run.files.length === 1 ? '' : 's'} ·
policy: ${escapeHtml(profileName)} · Unicode ${escapeHtml(UCD_VERSION)}</p>

<h2>Hidden messages</h2>
${
  payloads.length
    ? payloads.join('')
    : '<p>No decodable hidden message was found. That is not the same as “this file is safe” — see the note at the end.</p>'
}

<h2>What was found</h2>
${summaryRows ? `<table><thead><tr><th>Kind</th><th>Consequence</th><th>Count</th></tr></thead><tbody>${summaryRows}</tbody></table>` : '<p>Nothing.</p>'}

<h2>Files</h2>
<table><thead><tr><th>File</th><th>Read as</th><th>Encoding</th><th>Characters</th><th>Findings</th></tr></thead><tbody>${perFile}</tbody></table>

${
  occurrences.length
    ? `<h2>Every occurrence</h2>
<table><thead><tr><th>File</th><th>Line</th><th>Col</th><th>Byte</th><th>Code point</th><th>Kind</th><th>Why</th></tr></thead><tbody>${occurrences.join('')}</tbody></table>`
    : ''
}

${
  htmlBlocks
    ? `<h2>Text hidden by CSS</h2>
<p class="mech">Same-document rules only. Anything applied by an external stylesheet, by JavaScript, or inside a shadow root is invisible to this pass.</p>
<table><thead><tr><th>File</th><th>Element</th><th>How</th><th>Rule</th><th>Text</th></tr></thead><tbody>${htmlBlocks}</tbody></table>`
    : ''
}

${
  pdfBlocks
    ? `<h2>Invisible text in a PDF</h2>
<table><thead><tr><th>File</th><th>Page</th><th>Why invisible</th><th>Glyphs</th><th>Text</th></tr></thead><tbody>${pdfBlocks}</tbody></table>`
    : ''
}

<h2>Notes</h2>
${HONESTY}

<footer>Produced by <strong>charsight</strong>, entirely in a browser — this report was never
uploaded anywhere. charsight.benrichardson.dev · benrichardson.dev</footer>
</body></html>`;
}
